import PictureInPictureAltIcon from "@mui/icons-material/PictureInPictureAlt";
import Box from "@mui/material/Box";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { createRoot, type Root } from "react-dom/client";
import { useNavigate } from "react-router-dom";

import WebPipWindow from "@/components/WebPipWindow.tsx";
import { ampli } from "@/config";
import { useDispatchEnabled } from "@/hooks/dispatch/useDispatchEnabled";
import {
  type ChannelList,
  type ChannelOrderType,
  type ChannelTypeType,
  channelListCollections,
  computeChannelList,
} from "@/models/repositories";
import { MyAccountContext } from "@/models/StateProviders/myAccountProvider.tsx";
import { WorkspaceContext } from "@/models/StateProviders/workspaceProvider.tsx";
import { useCollectionsCompute } from "@/store/useCollectionsCompute";
import { ipcRendererCallback, ipcRendererSend, isElectron, removeIpcListener } from "@/utils";

// @ts-ignore
const { documentPictureInPicture } = window;

const loadPipWindowWeb = async (): Promise<Window> =>
  documentPictureInPicture.requestWindow({
    width: 350,
    height: 500,
  });

export default function ToggleSecondWindowButton() {
  const navigate = useNavigate();
  const [pipWindowOpen, setPipWindowOpen] = useState<boolean>(false);
  const { myAccount } = useContext(MyAccountContext);
  const { currentWorkspaceId, myCurrentWorkspaceMembership } = useContext(WorkspaceContext);
  const dispatchEnabled = useDispatchEnabled(currentWorkspaceId ?? undefined);

  const myChannels = useCollectionsCompute(
    channelListCollections,
    () =>
      currentWorkspaceId && myCurrentWorkspaceMembership?.id && myAccount?.id
        ? computeChannelList({
            channelOrder: "driver-activity" as ChannelOrderType,
            channelType: "my-channels" as ChannelTypeType,
            workspaceId: currentWorkspaceId,
            myCurrentWorkspaceMembership,
            searchValue: "",
            myAccount,
            channelFilters: [],
            selectedFeedGroups: [],
          })
        : null,
    [currentWorkspaceId, myCurrentWorkspaceMembership?.id, myAccount?.id],
    "ToggleSecondWindowButton:myChannels",
  );

  // The store rows carry the same runtime shape the old SQL rows did; ChannelList remains the
  // consumer-facing contract (nullable aggregate fields included).
  const myFilteredChannels = useMemo(
    () => (myChannels?.filter((c) => c.unread !== null) ?? null) as unknown as ChannelList[] | null,
    [myChannels],
  );

  const webPipWindowRef = useRef<Window | null>(null);
  const pipRootRef = useRef<Root | null>(null);

  const togglePipMode = (value: boolean) => {
    ampli.activatePiP();

    if (isElectron) {
      setPipWindowOpen(!value);
      const secondWindowState = !value ? "second-window-open" : "second-window-close";
      ipcRendererSend(secondWindowState);
    } else {
      if (webPipWindowRef.current && !webPipWindowRef.current.closed) {
        pipRootRef.current?.unmount();
        pipRootRef.current = null;
        webPipWindowRef.current.close();
        webPipWindowRef.current = null;
        setPipWindowOpen(false);
      } else {
        if (!documentPictureInPicture) {
          return;
        }
        loadPipWindowWeb()
          .then((pipWindow) => {
            webPipWindowRef.current = pipWindow;
            pipWindow.document.documentElement.className = "pip-window-html";
            pipWindow.document.body.className = "pip-window-body";
            const linkElement = document.createElement("link");
            linkElement.rel = "stylesheet";
            linkElement.href = "/pip-window.css";
            linkElement.type = "text/css";
            pipWindow.document.head.appendChild(linkElement);

            const container = document.createElement("div");
            container.id = "pip-root";
            pipWindow.document.body.appendChild(container);

            pipRootRef.current = createRoot(container);
            pipRootRef.current.render(
              <WebPipWindow clickItem={focusAndNavigate} data={myFilteredChannels} dispatchEnabled={dispatchEnabled} />,
            );

            setPipWindowOpen(true);

            pipWindow.addEventListener("pagehide", () => {
              pipRootRef.current?.unmount();
              pipRootRef.current = null;
              setPipWindowOpen(false);
              webPipWindowRef.current?.close();
              webPipWindowRef.current = null;
            });
          })
          .catch(() => {
            setPipWindowOpen(false);
          });
      }
    }
  };

  const handleSecondWindowReady = (value: string) => {
    ipcRendererSend("container-change", myFilteredChannels);
  };

  const handleSecondWindowChange = useCallback((value?: boolean) => {
    setPipWindowOpen(() => value);
    return value;
  }, []);

  useEffect(() => {
    ipcRendererCallback("second-window-change", (_, value: boolean) => handleSecondWindowChange(value));
    ipcRendererCallback("second-window-ready", (_, value: string) => handleSecondWindowReady(value));
    ipcRendererSend("container-change", myFilteredChannels);
    return () => {
      removeIpcListener("second-window-change", () => handleSecondWindowChange);
      removeIpcListener("second-window-ready", () => handleSecondWindowReady);
    };
  }, [myFilteredChannels, setPipWindowOpen]);

  const focusAndNavigate = useCallback(
    (value: string) => {
      window.focus();
      navigate(value);
    },
    [navigate],
  );

  // Re-render PIP window content when data changes
  useEffect(() => {
    if (pipRootRef.current && webPipWindowRef.current) {
      pipRootRef.current.render(
        <WebPipWindow clickItem={focusAndNavigate} data={myFilteredChannels} dispatchEnabled={dispatchEnabled} />,
      );
    }
  }, [myFilteredChannels, focusAndNavigate]);

  // Cleanup PIP window on component unmount
  useEffect(() => {
    return () => {
      if (pipRootRef.current) {
        pipRootRef.current.unmount();
        pipRootRef.current = null;
      }
      if (webPipWindowRef.current) {
        webPipWindowRef.current.close();
        webPipWindowRef.current = null;
      }
    };
  }, []);

  return (
    <Box
      component="button"
      sx={{
        ...(pipWindowOpen ? { background: "var(--color-brand-primary)" } : { background: "none" }),
        p: 1,
        borderRadius: 1,
        cursor: "pointer",
        border: "none",
        color: "inherit",
      }}
      onClick={() => togglePipMode(pipWindowOpen)}
    >
      <PictureInPictureAltIcon />
    </Box>
  );
}
